/* import __COLOCATED_TEMPLATE__ from './standalone-admin-invites.hbs'; */
/* RESPONSIBLE TEAM: team-pricing-and-packaging */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { TrackedSet } from 'tracked-built-ins';
import type AppService from 'embercom/services/app-service';
import type Store from '@ember-data/store';

interface Signature {
  Args: {
    isVisible: boolean;
    onClose: () => void;
    onImport: (emails: string[]) => void;
    platformName: string;
  };
}

interface StandaloneAdmin {
  id: string;
  name: string;
  derivedRealEmail: string;
  isSelected: boolean;
}

export default class StandaloneAdminInvites extends Component<Signature> {
  @service declare appService: AppService;
  @service declare store: Store;

  selectedIds = new TrackedSet<string>();

  get app() {
    return this.appService.app;
  }

  get invitableAdmins(): StandaloneAdmin[] {
    let standaloneAdmins = this.app.standaloneDummyAdmins || [];
    let normalAdminEmails = new Set(
      (this.app.humanAdmins || []).map((admin: $TSFixMe) => admin.email?.toLowerCase()),
    );
    let invitedEmails = new Set(
      this.store
        .peekAll('invited-admin')
        .filter((invite: $TSFixMe) => invite.active)
        .map((invite: $TSFixMe) => invite.email?.toLowerCase()),
    );

    return standaloneAdmins
      .filter((admin: $TSFixMe) => {
        let email = admin.email;
        if (!email || !email.includes('.hirefin.com')) {
          return false;
        }
        let derivedRealEmail = email.replace('.hirefin.com', '').toLowerCase();
        return !normalAdminEmails.has(derivedRealEmail) && !invitedEmails.has(derivedRealEmail);
      })
      .map((admin: $TSFixMe) => {
        let derivedRealEmail = admin.email.replace('.hirefin.com', '');
        return {
          id: admin.id,
          name: admin.name || derivedRealEmail,
          derivedRealEmail,
          isSelected: this.selectedIds.has(admin.id),
        };
      });
  }

  get hasInvitableAdmins(): boolean {
    return this.invitableAdmins.length > 0;
  }

  get selectedCount(): number {
    return this.selectedIds.size;
  }

  get allSelected(): boolean {
    return this.invitableAdmins.length > 0 && this.selectedIds.size === this.invitableAdmins.length;
  }

  get importDisabled(): boolean {
    return this.selectedCount === 0;
  }

  @action
  toggleAdmin(adminId: string) {
    if (this.selectedIds.has(adminId)) {
      this.selectedIds.delete(adminId);
    } else {
      this.selectedIds.add(adminId);
    }
  }

  @action
  toggleSelectAll() {
    if (this.allSelected) {
      this.selectedIds.clear();
    } else {
      for (let admin of this.invitableAdmins) {
        this.selectedIds.add(admin.id);
      }
    }
  }

  @action
  importSelected() {
    let selectedAdmins = this.invitableAdmins.filter((admin) => this.selectedIds.has(admin.id));
    if (selectedAdmins.length === 0) {
      return;
    }

    let emails = selectedAdmins.map((admin) => admin.derivedRealEmail);
    this.args.onImport(emails);
    this.selectedIds.clear();
    this.args.onClose();
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'NewSettings::Workspace::Teammates::StandaloneAdminInvites': typeof StandaloneAdminInvites;
  }
}
